import { useParams, Link } from 'react-router-dom';
import { news } from '../../mocks/news';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import { useEffect } from 'react';

const ArticlePage = () => {
  const { id } = useParams<{ id: string }>();
  const article = news.find((n) => n.id === Number(id));

  const handleBackClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    window.REACT_APP_NAVIGATE('/');
    setTimeout(() => {
      const newsSection = document.getElementById('news');
      if (newsSection) {
        newsSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  useEffect(() => {
    // Load Instagram embed script
    if (article?.instagramEmbed) {
      const script = document.createElement('script');
      script.src = '//www.instagram.com/embed.js';
      script.async = true;
      document.body.appendChild(script);

      // Process embeds after script loads
      script.onload = () => {
        if (window.instgrm) {
          window.instgrm.Embeds.process();
        }
      };

      return () => {
        if (document.body.contains(script)) {
          document.body.removeChild(script);
        }
      };
    }
  }, [article]);

  if (!article) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-white mb-4">Article Not Found</h1>
          <Link to="/" className="text-[#8B0000] hover:text-[#6B0000]">
            Return Home
          </Link>
        </div>
      </div>
    );
  }

  const renderArticleContent = () => {
    if (id === '1') {
      return (
        <div className="space-y-8">
          <p className="text-gray-300 leading-relaxed text-lg">
            Zane "Nightmare" Morehouse's mental toughness wasn't forged on the baseball diamond—it was built in a boxing gym. Growing up surrounded by fighters who understood what it meant to take a punch and keep moving forward, Zane developed a psychological resilience that sets him apart on the mound.
          </p>

          <blockquote className="text-2xl italic text-[#8B0000] font-serif my-8 pl-6 border-l-4 border-[#8B0000]">
            "The game is meant to be easy, it's the preparation that should be the hardest."
          </blockquote>

          <p className="text-gray-300 leading-relaxed text-lg">
            Those words come from his father, Allen Morehouse—a retired PBR bull rider turned biomechanist who serves as Zane's trainer. Allen knows something about facing fear head-on. After years of climbing onto 1,800-pound bulls that wanted nothing more than to throw him off, he brought that same intensity and discipline to training his son.
          </p>

          <div className="bg-[#141414] border border-gray-800 rounded-lg p-6 my-8">
            <h3 className="text-xl font-semibold text-white mb-4">The Boxing Gym Philosophy</h3>
            <p className="text-gray-300 leading-relaxed mb-4">
              In boxing, you learn quickly that physical talent alone won't carry you through. It takes mental fortitude to get punched in the mouth and keep your composure, to stick to your game plan when everything in your body is screaming to quit. That's the environment where Zane learned to compete.
            </p>
            <p className="text-gray-300 leading-relaxed">
              "You can't flinch," Zane explains. "Whether it's taking a punch or facing a bases-loaded situation with no outs, you have to stay present. The moment you start thinking about what might go wrong, you've already lost."
            </p>
          </div>

          <h3 className="text-2xl font-semibold text-white mb-4 mt-10">The Pressure Principle</h3>
          
          <p className="text-gray-300 leading-relaxed text-lg">
            No one puts more pressure on Zane than his father. But it's not the destructive kind of pressure that breaks athletes—it's the pressure that forges diamonds. Allen's philosophy is simple but demanding: if you want the game to feel easy, you have to make the preparation impossibly hard.
          </p>

          <p className="text-gray-300 leading-relaxed text-lg mt-6">
            This means grueling training sessions that push beyond physical limits. It means studying film until your eyes blur. It means throwing bullpen sessions in conditions that would make most pitchers pack it in. When game day arrives, everything feels lighter by comparison.
          </p>

          <div className="bg-[#141414] border border-gray-800 rounded-lg p-6 my-8">
            <h3 className="text-xl font-semibold text-white mb-4">Pre-Game Mental Routine</h3>
            <ul className="space-y-4 text-gray-300">
              <li className="flex items-start">
                <i className="ri-focus-3-line text-[#8B0000] text-xl mt-1 mr-3"></i>
                <div>
                  <strong className="text-white">Visualization:</strong> Zane spends 20 minutes before each start visualizing every pitch, every situation, every possible outcome. He sees himself succeeding, but also handles adversity in his mind first.
                </div>
              </li>
              <li className="flex items-start">
                <i className="ri-heart-pulse-line text-[#8B0000] text-xl mt-1 mr-3"></i>
                <div>
                  <strong className="text-white">Breathing Control:</strong> Techniques learned from boxing help him control his heart rate and stay calm under pressure. Four counts in, hold for four, four counts out.
                </div>
              </li>
              <li className="flex items-start">
                <i className="ri-shield-check-line text-[#8B0000] text-xl mt-1 mr-3"></i>
                <div>
                  <strong className="text-white">Embrace the Pressure:</strong> Rather than trying to eliminate nerves, Zane reframes them as excitement. The butterflies mean it matters. The tension means he's ready to compete.
                </div>
              </li>
            </ul>
          </div>

          <h3 className="text-2xl font-semibold text-white mb-4 mt-10">Handling Adversity</h3>

          <p className="text-gray-300 leading-relaxed text-lg">
            Every pitcher gives up hits. Every pitcher has bad outings. What separates the good from the great is how they respond. Zane's boxing gym upbringing taught him that getting knocked down isn't failure—staying down is.
          </p>

          <p className="text-gray-300 leading-relaxed text-lg mt-6">
            "When a hitter takes me deep, I treat it like taking a jab," Zane says. "It stings for a second, but you can't dwell on it. You have to reset immediately and focus on the next pitch. That's all that matters—the next pitch."
          </p>

          <div className="bg-[#141414] border border-gray-800 rounded-lg p-6 my-8">
            <h3 className="text-xl font-semibold text-white mb-4">The Father-Son Dynamic</h3>
            <p className="text-gray-300 leading-relaxed mb-4">
              Allen Morehouse's transition from bull riding to biomechanics wasn't accidental. After years of studying how to stay balanced on an animal designed to throw him off, he became fascinated with human movement, efficiency, and the mental game required to perform under extreme stress.
            </p>
            <p className="text-gray-300 leading-relaxed mb-4">
              As Zane's trainer, Allen brings a unique perspective. He understands fear—real, physical fear—and how to channel it into performance. He knows what it means to prepare for something that could hurt you, and to do it anyway with confidence and precision.
            </p>
            <p className="text-gray-300 leading-relaxed">
              "My dad never lets me take the easy route," Zane admits. "If there's a harder way to do something that will make me better, that's the way we're doing it. It's exhausting, but when I'm on the mound in a tight game, I know I've been through worse in training. That confidence is everything."
            </p>
          </div>

          <h3 className="text-2xl font-semibold text-white mb-4 mt-10">The Nightmare Mentality</h3>

          <p className="text-gray-300 leading-relaxed text-lg">
            The nickname "Nightmare" isn't just about what Zane does to opposing hitters—it's about his mindset. While batters are worried about facing him, he's already won the mental battle. He's been through preparation so demanding that the game itself feels like a release.
          </p>

          <div className="w-full h-96 overflow-hidden rounded-lg my-8">
            <img
              src="https://static.readdy.ai/image/4abb71d54c7d60621cfc0b2fab4f7970/ded02fcadea41acfca3384dbc10eaaf2.jpeg"
              alt="Zane Morehouse Mental Game"
              className="w-full h-full object-cover object-top"
            />
          </div>

          <p className="text-gray-300 leading-relaxed text-lg">
            This mental approach, forged in a boxing gym and refined by a father who demands excellence, is what makes Zane Morehouse more than just a talented pitcher. It's what makes him a competitor who thrives when the pressure is highest, who gets stronger when others fold, and who treats every challenge as an opportunity to prove that the hardest work happens long before the first pitch is thrown.
          </p>

          <div className="bg-gradient-to-r from-[#8B0000]/10 to-transparent border-l-4 border-[#8B0000] p-6 my-8">
            <p className="text-gray-300 leading-relaxed italic">
              "In boxing, they say everyone has a plan until they get punched in the mouth. In baseball, everyone has confidence until they face adversity. The difference is, I've been training for that punch my whole life. When it comes, I'm ready."
            </p>
            <p className="text-gray-500 text-sm mt-4">— Zane "Nightmare" Morehouse</p>
          </div>
        </div>
      );
    }

    if (id === '2') {
      return (
        <div className="space-y-8">
          <p className="text-gray-300 leading-relaxed text-lg">
            Zane "Nightmare" Morehouse delivered a dominant performance, striking out 4 batters in just 2 innings of work. His electric fastball and devastating breaking pitches kept hitters off balance throughout his appearance, showcasing the elite stuff that has scouts buzzing about his future potential.
          </p>

          <div className="bg-[#141414] border border-gray-800 rounded-lg p-6 my-8">
            <h3 className="text-xl font-semibold text-white mb-4">Performance Highlights</h3>
            <ul className="space-y-3 text-gray-300">
              <li className="flex items-start">
                <i className="ri-checkbox-circle-fill text-[#8B0000] mt-1 mr-3"></i>
                <span><strong>4 strikeouts</strong> in just 2 innings pitched</span>
              </li>
              <li className="flex items-start">
                <i className="ri-checkbox-circle-fill text-[#8B0000] mt-1 mr-3"></i>
                <span><strong>Electric fastball</strong> reaching peak velocity</span>
              </li>
              <li className="flex items-start">
                <i className="ri-checkbox-circle-fill text-[#8B0000] mt-1 mr-3"></i>
                <span><strong>Devastating breaking pitches</strong> keeping hitters off balance</span>
              </li>
            </ul>
          </div>

          <p className="text-gray-300 leading-relaxed text-lg">
            This outing continues to demonstrate why Nightmare has become one of the most feared arms in the organization. His ability to dominate in high-leverage situations shows the mental toughness and elite stuff that scouts have been raving about all season.
          </p>
        </div>
      );
    }

    if (id === '3') {
      return (
        <div className="space-y-8">
          <p className="text-gray-300 leading-relaxed text-lg">
            In one of the most spectacular defensive plays of the season, Zane "Nightmare" Morehouse was at the heart of a rare triple play that left fans and teammates in absolute disbelief. With the bases loaded and no outs, the situation looked dire for the Akron Rubber Ducks. What happened next was pure baseball magic.
          </p>

          <blockquote className="text-2xl italic text-[#8B0000] font-serif my-8 pl-6 border-l-4 border-[#8B0000]">
            "I just reacted. Knocked it down, picked it up, and started the chain reaction. The rest was pure instinct and trust in my teammates."
          </blockquote>

          <p className="text-gray-300 leading-relaxed text-lg">
            A sharp line drive was hit directly at Nightmare on the mound. With lightning-fast reflexes, he knocked the ball down, quickly scooped it up, and immediately recognized the opportunity. With runners moving on contact and the bases loaded, he fired home for the first force out. The catcher, showing exceptional awareness, gunned the ball to first base to retire the batter for the second out. But the play wasn't over yet.
          </p>

          <div className="bg-[#141414] border border-gray-800 rounded-lg p-6">
            <h3 className="text-xl font-semibold text-white mb-4">The Triple Play Sequence</h3>
            <ul className="space-y-4 text-gray-300">
              <li className="flex items-start">
                <div className="w-8 h-8 bg-[#8B0000] rounded-full flex items-center justify-center text-white font-bold mr-4 mt-1 flex-shrink-0">1</div>
                <div>
                  <strong className="text-white">The Knockdown:</strong> Line drive hit to Nightmare on the mound. He knocks it down with quick reflexes, picks it up cleanly.
                </div>
              </li>
              <li className="flex items-start">
                <div className="w-8 h-8 bg-[#8B0000] rounded-full flex items-center justify-center text-white font-bold mr-4 mt-1 flex-shrink-0">2</div>
                <div>
                  <strong className="text-white">Force Out at Home:</strong> Nightmare fires to home plate for the first force out as the runner from third tries to score.
                </div>
              </li>
              <li className="flex items-start">
                <div className="w-8 h-8 bg-[#8B0000] rounded-full flex items-center justify-center text-white font-bold mr-4 mt-1 flex-shrink-0">3</div>
                <div>
                  <strong className="text-white">Relay to First:</strong> Catcher immediately throws to first base to retire the batter for the second force out.
                </div>
              </li>
              <li className="flex items-start">
                <div className="w-8 h-8 bg-[#8B0000] rounded-full flex items-center justify-center text-white font-bold mr-4 mt-1 flex-shrink-0">4</div>
                <div>
                  <strong className="text-white">Back to Home:</strong> First baseman sees the runner from second base trying to score and fires back to home. Catcher applies the tag to complete the triple play!
                </div>
              </li>
            </ul>
          </div>

          <p className="text-gray-300 leading-relaxed text-lg mt-8">
            The most remarkable part? The runner who started at second base, seeing the chaos unfold, attempted to take home. But the first baseman spotted him and fired back to the catcher, who applied the tag for the third out. Three outs on one play. Bases loaded, no outs to three outs in a matter of seconds.
          </p>

          <div className="bg-gradient-to-r from-[#8B0000]/10 to-transparent border-l-4 border-[#8B0000] p-6 my-8">
            <h3 className="text-xl font-semibold text-white mb-3">By The Numbers</h3>
            <div className="grid grid-cols-2 gap-6 text-gray-300">
              <div>
                <p className="text-4xl font-bold text-[#8B0000] mb-1">1-2-3-2</p>
                <p className="text-sm text-gray-500 uppercase tracking-wider">Scoring Sequence</p>
                <p className="text-xs text-gray-600 mt-1">Pitcher → Catcher → First → Catcher</p>
              </div>
              <div>
                <p className="text-4xl font-bold text-[#8B0000] mb-1">~8 sec</p>
                <p className="text-sm text-gray-500 uppercase tracking-wider">Play Duration</p>
                <p className="text-xs text-gray-600 mt-1">From contact to final tag</p>
              </div>
            </div>
          </div>

          <div className="mt-10">
            <h3 className="text-2xl font-semibold text-white mb-4">The Aftermath</h3>
            <p className="text-gray-300 leading-relaxed text-lg">
              The stadium erupted. Teammates poured out of the dugout. What looked like a bases-loaded, no-out rally turned into three outs in the blink of an eye. This wasn't just a defensive play—it was a momentum-killer, a rally-crusher, and a highlight that will be replayed for years to come.
            </p>
            <p className="text-gray-300 leading-relaxed text-lg mt-6">
              Triple plays are among the rarest events in baseball, occurring roughly once every 7,000 games. To be at the center of one requires not just skill, but split-second decision-making, perfect execution, and complete trust in your teammates. Nightmare and the Akron Rubber Ducks delivered on all fronts.
            </p>
          </div>

          <div className="bg-[#141414] border border-gray-800 rounded-lg p-6 my-8">
            <h3 className="text-xl font-semibold text-white mb-4">What Makes This Play Special</h3>
            <ul className="space-y-3 text-gray-300">
              <li className="flex items-start">
                <i className="ri-star-fill text-[#8B0000] mt-1 mr-3"></i>
                <span><strong className="text-white">Bases Loaded, No Outs:</strong> The highest pressure situation turned into three outs instantly</span>
              </li>
              <li className="flex items-start">
                <i className="ri-star-fill text-[#8B0000] mt-1 mr-3"></i>
                <span><strong className="text-white">Three Players Involved:</strong> Pitcher, catcher, and first baseman all touched the ball with perfect timing</span>
              </li>
              <li className="flex items-start">
                <i className="ri-star-fill text-[#8B0000] mt-1 mr-3"></i>
                <span><strong className="text-white">Aggressive Baserunning Backfires:</strong> The runner from second trying to score sealed the triple play</span>
              </li>
              <li className="flex items-start">
                <i className="ri-star-fill text-[#8B0000] mt-1 mr-3"></i>
                <span><strong className="text-white">Complete Momentum Shift:</strong> Turned a potential big inning into an inning-ending highlight</span>
              </li>
            </ul>
          </div>

          <p className="text-gray-300 leading-relaxed text-lg">
            For Zane "Nightmare" Morehouse, this play exemplifies everything that makes him special. Quick reflexes, baseball IQ, composure under pressure, and the ability to make the right play at the right time. It's moments like these that remind us why they call him Nightmare—because for opposing teams, plays like this are exactly that.
          </p>
        </div>
      );
    }

    if (id === '4') {
      return (
        <div className="space-y-8">
          <p className="text-gray-300 leading-relaxed text-lg">
            In a significant milestone for his professional career, Zane "Nightmare" Morehouse has been promoted to Double-A Akron, marking a major step forward in his journey through the Cleveland Guardians organization. The promotion comes after an impressive showing at Single-A, where Nightmare demonstrated the elite stuff, mental toughness, and consistent performance that caught the attention of the organization's decision-makers.
          </p>

          <blockquote className="text-2xl italic text-[#8B0000] font-serif my-8 pl-6 border-l-4 border-[#8B0000]">
            "This is what all the hard work has been for. Double-A is where you prove you belong. I'm ready for the challenge."
          </blockquote>

          <div className="bg-[#141414] border border-gray-800 rounded-lg p-6 my-8">
            <h3 className="text-xl font-semibold text-white mb-4">What This Promotion Means</h3>
            <ul className="space-y-4 text-gray-300">
              <li className="flex items-start">
                <i className="ri-trophy-line text-[#8B0000] text-xl mt-1 mr-3"></i>
                <div>
                  <strong className="text-white">Recognition of Performance:</strong> The promotion validates Nightmare's dominant performance at Single-A, where he consistently overpowered hitters with his electric fastball and devastating breaking pitches.
                </div>
              </li>
              <li className="flex items-start">
                <i className="ri-arrow-up-circle-line text-[#8B0000] text-xl mt-1 mr-3"></i>
                <div>
                  <strong className="text-white">Higher Level Competition:</strong> Double-A represents a significant jump in competition, featuring more experienced hitters with better plate discipline and the ability to make adjustments.
                </div>
              </li>
              <li className="flex items-start">
                <i className="ri-map-pin-line text-[#8B0000] text-xl mt-1 mr-3"></i>
                <div>
                  <strong className="text-white">Closer to The Show:</strong> Double-A is often considered the most important proving ground for prospects. Success here puts players just one step away from Triple-A and the major leagues.
                </div>
              </li>
              <li className="flex items-start">
                <i className="ri-team-line text-[#8B0000] text-xl mt-1 mr-3"></i>
                <div>
                  <strong className="text-white">Joining the Rubber Ducks:</strong> The Akron RubberDucks are the Double-A affiliate of the Cleveland Guardians, known for their excellent player development and competitive environment.
                </div>
              </li>
            </ul>
          </div>

          <h3 className="text-2xl font-semibold text-white mb-4 mt-10">The Journey to Double-A</h3>

          <p className="text-gray-300 leading-relaxed text-lg">
            Nightmare's path to this promotion has been marked by consistent excellence and steady improvement. At Single-A, he showcased the kind of dominance that makes scouts take notice—overpowering fastballs, sharp breaking balls, and the mental fortitude to execute in high-pressure situations.
          </p>

          <p className="text-gray-300 leading-relaxed text-lg mt-6">
            His training regimen, overseen by his father Allen Morehouse—a retired PBR bull rider turned biomechanist—has been instrumental in his rapid development. The demanding preparation that makes game days feel easy has prepared him for this next challenge.
          </p>

          <div className="w-full h-96 overflow-hidden rounded-lg my-8">
            <img
              src="https://static.readdy.ai/image/4abb71d54c7d60621cfc0b2fab4f7970/7cd6111c07df4c07ef6df52ceca42e94.jpeg"
              alt="Zane Morehouse Double-A Promotion"
              className="w-full h-full object-cover object-top"
            />
          </div>

          <h3 className="text-2xl font-semibold text-white mb-4 mt-10">What to Expect at Double-A</h3>

          <p className="text-gray-300 leading-relaxed text-lg">
            Double-A baseball represents a significant step up in competition. Hitters at this level have better plate discipline, more experience recognizing pitches, and the ability to make in-game adjustments. For Nightmare, this means facing batters who won't chase pitches out of the zone as readily and who can punish mistakes.
          </p>

          <div className="bg-[#141414] border border-gray-800 rounded-lg p-6 my-8">
            <h3 className="text-xl font-semibold text-white mb-4">The Double-A Challenge</h3>
            <ul className="space-y-3 text-gray-300">
              <li className="flex items-start">
                <i className="ri-focus-2-line text-[#8B0000] mt-1 mr-3"></i>
                <span><strong className="text-white">Better Hitters:</strong> More experienced batters with refined approaches and better pitch recognition</span>
              </li>
              <li className="flex items-start">
                <i className="ri-eye-line text-[#8B0000] mt-1 mr-3"></i>
                <span><strong className="text-white">Increased Scrutiny:</strong> More scouts, more attention, and higher expectations for performance</span>
              </li>
              <li className="flex items-start">
                <i className="ri-bar-chart-box-line text-[#8B0000] mt-1 mr-3"></i>
                <span><strong className="text-white">Development Focus:</strong> Emphasis on refining secondary pitches and improving command</span>
              </li>
              <li className="flex items-start">
                <i className="ri-mental-health-line text-[#8B0000] mt-1 mr-3"></i>
                <span><strong className="text-white">Mental Game:</strong> Learning to handle adversity and make adjustments at a higher level</span>
              </li>
            </ul>
          </div>

          <h3 className="text-2xl font-semibold text-white mb-4 mt-10">The Guardians' Development System</h3>

          <p className="text-gray-300 leading-relaxed text-lg">
            The Cleveland Guardians organization has a well-earned reputation for developing elite pitching talent. Their player development staff emphasizes pitch design, biomechanics, and data-driven approaches to improvement—all areas where Nightmare excels.
          </p>

          <p className="text-gray-300 leading-relaxed text-lg mt-6">
            At Akron, Nightmare will have access to advanced technology, experienced coaches, and a competitive environment that will push him to refine every aspect of his game. The organization's track record of developing pitchers who reach the major leagues speaks to the quality of instruction and support he'll receive.
          </p>

          <div className="bg-gradient-to-r from-[#8B0000]/10 to-transparent border-l-4 border-[#8B0000] p-6 my-8">
            <h3 className="text-xl font-semibold text-white mb-3">Next Steps</h3>
            <ul className="space-y-3 text-gray-300">
              <li className="flex items-start">
                <i className="ri-checkbox-line text-[#8B0000] mt-1 mr-3"></i>
                <span>Establish consistency against Double-A competition</span>
              </li>
              <li className="flex items-start">
                <i className="ri-checkbox-line text-[#8B0000] mt-1 mr-3"></i>
                <span>Continue refining secondary pitches and command</span>
              </li>
              <li className="flex items-start">
                <i className="ri-checkbox-line text-[#8B0000] mt-1 mr-3"></i>
                <span>Build innings and durability for higher workloads</span>
              </li>
              <li className="flex items-start">
                <i className="ri-checkbox-line text-[#8B0000] mt-1 mr-3"></i>
                <span>Prove readiness for eventual Triple-A promotion</span>
              </li>
            </ul>
          </div>

          <h3 className="text-2xl font-semibold text-white mb-4 mt-10">Looking Forward</h3>

          <p className="text-gray-300 leading-relaxed text-lg">
            This promotion to Double-A Akron represents more than just a change in uniform—it's a validation of Nightmare's work ethic, talent, and potential. It's a statement from the organization that they believe in his ability to compete at higher levels and eventually reach the major leagues.
          </p>

          <p className="text-gray-300 leading-relaxed text-lg mt-6">
            For Zane "Nightmare" Morehouse, the journey continues. The boxing gym mentality that has carried him this far—the mental toughness, the relentless preparation, the refusal to back down from any challenge—will be tested at this new level. But if his track record is any indication, he's more than ready for what comes next.
          </p>

          <div className="bg-gradient-to-r from-[#8B0000]/10 to-transparent border-l-4 border-[#8B0000] p-6 my-8">
            <p className="text-gray-300 leading-relaxed italic">
              "Every level is a new challenge, a new opportunity to prove yourself. Double-A is where you separate the prospects from the players. I didn't come this far to just be a prospect."
            </p>
            <p className="text-gray-500 text-sm mt-4">— Zane "Nightmare" Morehouse</p>
          </div>
        </div>
      );
    }

    if (id === '6') {
      return (
        <div className="space-y-8">
          <p className="text-gray-300 leading-relaxed text-lg">
            Baseball America has included Zane "Nightmare" Morehouse in their prestigious Top 100 Prospects list for 2025, cementing his status as one of the most exciting young arms in professional baseball. The recognition comes after a breakout season that saw him dominate across multiple levels, showcasing the rare combination of elite stuff, advanced command, and mental toughness that separates future major leaguers from the pack.
          </p>

          <div className="bg-[#141414] border border-gray-800 rounded-lg p-6 my-8">
            <h3 className="text-xl font-semibold text-white mb-4">What Makes Nightmare Elite</h3>
            <ul className="space-y-4 text-gray-300">
              <li className="flex items-start">
                <i className="ri-flashlight-fill text-[#8B0000] text-xl mt-1 mr-3"></i>
                <div>
                  <strong className="text-white">Electric Four-Pitch Arsenal:</strong> Nightmare's fastball sits 94-97 mph with late life, complemented by a devastating cutter-slider combo that generates swings and misses at an elite rate. His splitter and sinker give him weapons against both left and right-handed hitters.
                </div>
              </li>
              <li className="flex items-start">
                <i className="ri-brain-line text-[#8B0000] text-xl mt-1 mr-3"></i>
                <div>
                  <strong className="text-white">Advanced Pitchability:</strong> Beyond raw stuff, scouts rave about his ability to sequence pitches, work both sides of the plate, and adjust his game plan mid-outing. His baseball IQ is well beyond his years.
                </div>
              </li>
              <li className="flex items-start">
                <i className="ri-shield-star-line text-[#8B0000] text-xl mt-1 mr-3"></i>
                <div>
                  <strong className="text-white">Mental Toughness:</strong> Forged in a boxing gym and refined by his father's demanding training regimen, Nightmare thrives in high-pressure situations. He doesn't just survive adversity—he seeks it out.
                </div>
              </li>
              <li className="flex items-start">
                <i className="ri-line-chart-line text-[#8B0000] text-xl mt-1 mr-3"></i>
                <div>
                  <strong className="text-white">Rapid Development:</strong> His ability to make adjustments and implement coaching feedback has accelerated his timeline. What typically takes pitchers years to develop, Nightmare has mastered in months.
                </div>
              </li>
            </ul>
          </div>

          <h3 className="text-2xl font-semibold text-white mb-4 mt-10">Scout Reports</h3>
          
          <p className="text-gray-300 leading-relaxed text-lg">
            Multiple scouts have filed glowing reports on Morehouse's potential. One American League scout noted, "The stuff is obvious—mid-90s with movement, plus breaking balls, and a changeup that's getting better every outing. But what really stands out is his competitiveness. He attacks hitters with a plan and doesn't back down from contact. That's a major league mentality."
          </p>

          <div className="bg-gradient-to-r from-[#8B0000]/10 to-transparent border-l-4 border-[#8B0000] p-6 my-8">
            <p className="text-gray-300 leading-relaxed italic">
              "Morehouse has the kind of arsenal that plays at the highest level. His cutter-slider combination is devastating, and he's not afraid to throw any pitch in any count. Add in his competitive fire and work ethic, and you're looking at a future impact arm."
            </p>
            <p className="text-gray-500 text-sm mt-4">— National League Scout</p>
          </div>

          <h3 className="text-2xl font-semibold text-white mb-4 mt-10">The Numbers Tell the Story</h3>

          <p className="text-gray-300 leading-relaxed text-lg">
            Nightmare's 2024 season statistics back up the hype. With a 2.87 ERA across 145 innings pitched, he struck out 156 batters while maintaining excellent control. His 1.12 WHIP demonstrates his ability to limit baserunners, while his 12 wins and 8 saves showcase his versatility and effectiveness in multiple roles.
          </p>

          <div className="bg-[#141414] border border-gray-800 rounded-lg p-6 my-8">
            <h3 className="text-xl font-semibold text-white mb-4">Advanced Metrics</h3>
            <div className="grid grid-cols-2 gap-6 text-gray-300">
              <div>
                <p className="text-3xl font-bold text-[#8B0000] mb-1">32.4%</p>
                <p className="text-sm text-gray-500 uppercase tracking-wider">Strikeout Rate</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-[#8B0000] mb-1">6.2%</p>
                <p className="text-sm text-gray-500 uppercase tracking-wider">Walk Rate</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-[#8B0000] mb-1">48.7%</p>
                <p className="text-sm text-gray-500 uppercase tracking-wider">Ground Ball Rate</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-[#8B0000] mb-1">38.1%</p>
                <p className="text-sm text-gray-500 uppercase tracking-wider">Chase Rate</p>
              </div>
            </div>
          </div>

          <h3 className="text-2xl font-semibold text-white mb-4 mt-10">The Cleveland Connection</h3>

          <p className="text-gray-300 leading-relaxed text-lg">
            Being in the Cleveland Guardians organization provides Nightmare with an ideal development environment. The Guardians have a proven track record of developing elite pitching talent, and their player development staff has already helped him refine his mechanics and expand his pitch repertoire.
          </p>

          <p className="text-gray-300 leading-relaxed text-lg mt-6">
            The organization's emphasis on pitch design and data-driven development aligns perfectly with Nightmare's analytical approach to the game. His father Allen, a biomechanist, works closely with the Guardians' staff to optimize every aspect of Zane's delivery and pitch execution.
          </p>

          <div className="w-full h-96 overflow-hidden rounded-lg my-8">
            <img
              src="https://static.readdy.ai/image/4abb71d54c7d60621cfc0b2fab4f7970/9b2f81b4d8fd14f4e70903f666d97400.jpeg"
              alt="Zane Morehouse Cleveland Guardians"
              className="w-full h-full object-cover object-top"
            />
          </div>

          <h3 className="text-2xl font-semibold text-white mb-4 mt-10">The Road Ahead</h3>

          <p className="text-gray-300 leading-relaxed text-lg">
            With his inclusion in Baseball America's Top 100, Nightmare has officially arrived as one of baseball's premier prospects. The next step is proving he can dominate at the upper levels of the minor leagues and eventually make his major league debut.
          </p>

          <p className="text-gray-300 leading-relaxed text-lg mt-6">
            If his trajectory continues, Zane "Nightmare" Morehouse could find himself on a major league mound sooner rather than later. And when that day comes, opposing hitters will understand exactly why they call him Nightmare.
          </p>

          <div className="bg-[#141414] border border-gray-800 rounded-lg p-6 my-8">
            <h3 className="text-xl font-semibold text-white mb-4">What's Next</h3>
            <ul className="space-y-3 text-gray-300">
              <li className="flex items-start">
                <i className="ri-arrow-right-circle-fill text-[#8B0000] mt-1 mr-3"></i>
                <span>Continue development at Double-A and Triple-A levels</span>
              </li>
              <li className="flex items-start">
                <i className="ri-arrow-right-circle-fill text-[#8B0000] mt-1 mr-3"></i>
                <span>Refine changeup to complement elite fastball-breaking ball combo</span>
              </li>
              <li className="flex items-start">
                <i className="ri-arrow-right-circle-fill text-[#8B0000] mt-1 mr-3"></i>
                <span>Build innings and durability for potential major league workload</span>
              </li>
              <li className="flex items-start">
                <i className="ri-arrow-right-circle-fill text-[#8B0000] mt-1 mr-3"></i>
                <span>Potential major league debut in 2026</span>
              </li>
            </ul>
          </div>
        </div>
      );
    }

    return (
      <div className="prose prose-lg prose-invert max-w-none">
        <p className="text-gray-300 leading-relaxed text-lg">{article.excerpt}</p>
        <p className="text-gray-400 mt-6">Full article content coming soon...</p>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Navbar />
      
      <main className="pt-24 pb-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Button */}
          <Link 
            to="/#news" 
            onClick={handleBackClick}
            className="inline-flex items-center space-x-2 text-gray-400 hover:text-white transition-colors mb-8 group"
          >
            <i className="ri-arrow-left-line group-hover:-translate-x-1 transition-transform w-5 h-5 flex items-center justify-center"></i>
            <span>Back to News</span>
          </Link>

          {/* Article Header */}
          <header className="mb-10">
            <span className="text-[#8B0000] text-sm font-medium">{article.date}</span>
            <h1 className="text-4xl md:text-5xl font-bold text-white mt-2 mb-6 leading-tight">
              {article.title}
            </h1>
            <p className="text-xl text-gray-400">{article.excerpt}</p>
          </header>

          {/* Instagram Embed */}
          {article.instagramEmbed && (
            <div className="mb-12 flex justify-center">
              <blockquote 
                className="instagram-media" 
                data-instgrm-permalink={`${article.instagramEmbed}?utm_source=ig_embed&utm_campaign=loading`}
                data-instgrm-version="14"
                style={{
                  background: '#FFF',
                  border: 0,
                  borderRadius: '3px',
                  boxShadow: '0 0 1px 0 rgba(0,0,0,0.5),0 1px 10px 0 rgba(0,0,0,0.15)',
                  margin: '1px',
                  maxWidth: '540px',
                  minWidth: '326px',
                  padding: 0,
                  width: 'calc(100% - 2px)'
                }}
              />
            </div>
          )}

          {/* Video Embed */}
          {article.videoEmbed && (
            <div className="mb-12">
              {article.videoEmbed.includes('youtube.com') ? (
                <div className="aspect-video rounded-xl overflow-hidden">
                  <iframe
                    src={article.videoEmbed}
                    title={article.title}
                    className="w-full h-full"
                    allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              ) : article.videoEmbed.includes('mlb.com') ? (
                <div className="aspect-video rounded-xl overflow-hidden bg-[#1a1a1a] flex items-center justify-center">
                  <a 
                    href={article.videoEmbed}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex flex-col items-center space-y-4 text-center p-8 group"
                  >
                    <div className="w-20 h-20 bg-[#8B0000] rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                      <i className="ri-play-fill text-4xl text-white"></i>
                    </div>
                    <span className="text-white font-semibold text-lg">Watch on MLB.com</span>
                    <span className="text-gray-400 text-sm">Click to view the full video</span>
                  </a>
                </div>
              ) : (
                <div className="aspect-video rounded-xl overflow-hidden">
                  <iframe
                    src={article.videoEmbed}
                    title={article.title}
                    className="w-full h-full"
                    allowFullScreen
                  />
                </div>
              )}
            </div>
          )}

          {/* Featured Image */}
          {article.image && !article.videoEmbed && !article.instagramEmbed && (
            <div className="mb-12">
              <img 
                src={article.image} 
                alt={article.title}
                className="w-full h-[400px] object-cover object-top rounded-xl"
              />
            </div>
          )}

          {/* Article Content */}
          <article>
            {renderArticleContent()}
          </article>

          {/* Share Section */}
          <div className="mt-16 pt-8 border-t border-gray-800">
            <h4 className="text-white font-semibold mb-4">Share this article</h4>
            <div className="flex space-x-4">
              <button className="w-10 h-10 bg-[#1a1a1a] rounded-full flex items-center justify-center text-gray-400 hover:text-white hover:bg-[#8B0000] transition-all cursor-pointer">
                <i className="ri-twitter-x-fill w-5 h-5 flex items-center justify-center"></i>
              </button>
              <button className="w-10 h-10 bg-[#1a1a1a] rounded-full flex items-center justify-center text-gray-400 hover:text-white hover:bg-[#8B0000] transition-all cursor-pointer">
                <i className="ri-facebook-fill w-5 h-5 flex items-center justify-center"></i>
              </button>
              <button className="w-10 h-10 bg-[#1a1a1a] rounded-full flex items-center justify-center text-gray-400 hover:text-white hover:bg-[#8B0000] transition-all cursor-pointer">
                <i className="ri-link w-5 h-5 flex items-center justify-center"></i>
              </button>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

// TypeScript declaration for Instagram embed
declare global {
  interface Window {
    instgrm?: {
      Embeds: {
        process: () => void;
      };
    };
  }
}

export default ArticlePage;
